

import {
  IconHome,
  IconPlus,
  IconUser,
  IconShieldHalf,
  IconBuilding,
  IconUsers,
  IconCrown,
  IconAt,
  IconColorFilter,
  IconCalendarEvent,
  IconWorld,
  IconList,
  IconInbox,
  IconMail,
  IconSend,
  IconMailForward,
  IconMessage,
  IconLayoutDashboard,
  IconAdjustments,
  IconUserPlus,
  IconTarget,
  IconTargetArrow,
  IconAddressBook,
  IconPuzzle,
  IconBellRinging,
  IconHistory,
  IconDevices,
  IconArmchair,
  IconScale,
  IconBolt,
  IconTemplate,
  IconPaperclip,
  IconDeviceMobile,
  IconFileText,
  IconSparkles,
  IconTable,
  IconListDetails,
  IconMicrophone,
  IconBuildingStore,
  IconPhoto,
} from "@tabler/icons-react";
import { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import { useLocation } from "react-router-dom";
import { useAiAvailabilityStore } from "@/lib/ai/ai-store";
import { getSearchEvents } from "@/lib/api/handlers/EventHandling";
import { getCompanyRole } from "@/lib/api/handlers/CompanyRoleHandling";
import { getCompany } from "@/lib/api/handlers/CompanyHandling";
import { Plugin } from "@/types/enums/Plugin";
import { getTemplates } from "@/lib/api/handlers/EventTemplateHandling";
import { EventTemplate, TemplateSectionType } from "@/types/EventTemplate";
import { Capability } from "@/types/Capability";
import { isImmersiveChromeRoute } from "@/lib/immersiveRoutes";
import { useCapabilitiesOptional } from "@/providers/CapabilitiesProvider";
import { smsChannelEnabledOf } from "@/lib/sms/smsCapabilities";
import {
  resolveEventCaps,
  isPageVisible,
  firstAccessibleEventUrl,
  type GatedPage,
} from "@/lib/authz/eventLanding";

export type SearchItem = {
  icon: React.ElementType;
  label: string;
  category: string;
  breadcrumb?: string;
  url: string;
  archived?: boolean;
  eventId?: string;
  /** When set, selecting the row runs this instead of navigating to `url`. */
  action?: () => void;
};

export type SearchEvent = {
  eventId: string;
  name: string;
  plugins: string[];
  role: string;
  archived: boolean;
  /** CUSTOM-role effective capabilities (empty for standard roles, which the
   *  client resolves from `role`). Gates which pages this event exposes in ⌘K. */
  capabilities: string[];
};

type SearchTemplate = Pick<EventTemplate, "templateId" | "name" | "includedSections">;

export type ParsedQuery = {
  pageQuery: string;
  atTokens: string[];
  hasAt: boolean;
};

function parseQuery(raw: string): ParsedQuery {
  // @\S* (not @\S+) so a lone "@" is also stripped from pageQuery,
  // preventing empty results while the user is mid-typing an event name
  const atTokens = (raw.match(/@(\S+)/g) ?? [])
    .map((tok) => tok.slice(1))
    .filter(Boolean);
  const pageQuery = raw
    .replace(/@\S*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return { pageQuery, atTokens, hasAt: raw.includes("@") };
}

function editDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) =>
      i === 0 ? j : j === 0 ? i : 0,
    ),
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

function scoreItem(item: SearchItem, query: string): number {
  if (!query) return 0;
  const q = query.toLowerCase();
  const label = item.label.toLowerCase();
  const breadcrumb = (item.breadcrumb ?? "").toLowerCase();
  const category = item.category.toLowerCase();
  const fullText = `${label} ${breadcrumb} ${category}`;

  if (label === q) return 1000;

  let score = 0;

  if (label.startsWith(q)) score += 100;
  if (label.includes(q)) score += 60;

  const labelWords = label.split(/\s+/);
  if (labelWords.some((w) => w.startsWith(q))) score += 50;

  // Acronym match: "em" → "E-Mail Vorlagen", "gl" → "Gästeliste"
  const acronym = labelWords.map((w) => w[0] ?? "").join("");
  if (acronym.startsWith(q)) score += 40;

  if (breadcrumb.includes(q)) score += 20;
  if (category.includes(q)) score += 10;

  // Multi-word: every query word must hit somewhere in the full text
  const qWords = q.split(/\s+/).filter(Boolean);
  if (qWords.length > 1 && qWords.every((qw) => fullText.includes(qw))) {
    score += 30;
  }

  // Fuzzy fallback: edit distance ≤ 1 for queries ≥ 3 chars
  if (score === 0 && q.length >= 3) {
    for (const lw of labelWords) {
      if (lw.length >= 3 && editDistance(q, lw) <= 1) {
        score += 25;
        break;
      }
    }
  }

  return score;
}

export const useSearch = () => {
  const [searchState, setSearchState] = useState("");
  const [events, setEvents] = useState<SearchEvent[]>([]);
  const [templates, setTemplates] = useState<SearchTemplate[]>([]);
  const [companyRole, setCompanyRole] = useState<string>("");
  const [customerType, setCustomerType] = useState<string>("INDIVIDUAL");
  const [loaded, setLoaded] = useState(false);
  const [lockedEvent, setLockedEvent] = useState<SearchEvent | null>(null);
  const t = useTranslations("popup-search");
  const tTemplates = useTranslations("eventTemplates");
  const tUser = useTranslations("userSettings");
  const tAi = useTranslations("aiAssistant");
  const pathname = useLocation().pathname;
  // SMS plugin wave: the SMS page's row needs the platform switch, which only
  // an event shell's capabilities carry — the dashboard's ⌘K (no provider,
  // the fallback says false) never lists it.
  const smsChannelEnabled = smsChannelEnabledOf(useCapabilitiesOptional());

  // AI assistant entry: only when a provider says the drawer can open, and not
  // on immersive routes where the assistant has its own docked surface (mirrors
  // AiAssistantButton's gating so search never offers a no-op open).
  const aiAvailable = useAiAvailabilityStore((s) => s.available);
  const isImmersiveRoute = isImmersiveChromeRoute(pathname);
  const showAiItem = aiAvailable && !isImmersiveRoute;

  const currentEventId = useMemo(() => {
    const match = pathname.match(/^\/event\/([^/]+)/);
    return match ? match[1] : null;
  }, [pathname]);

  const loadData = useCallback(async () => {
    if (loaded) return;
    try {
      const [eventsResult, roleResult, companyResult, templatesResult] = await Promise.all([
        getSearchEvents(),
        getCompanyRole("role"),
        getCompany("customerType"),
        getTemplates(0, 100, ""),
      ]);
      if (Array.isArray(eventsResult)) {
        setEvents(eventsResult);
      }
      if (Array.isArray(templatesResult)) {
        setTemplates(
          templatesResult.map((template) => ({
            templateId: template.templateId,
            name: template.name,
            includedSections: template.includedSections ?? [],
          })),
        );
      }
      if (roleResult && !("isServerError" in roleResult)) {
        setCompanyRole(roleResult.role);
      }
      if (companyResult && !("isServerError" in companyResult)) {
        setCustomerType(companyResult.customerType ?? "INDIVIDUAL");
      }
    } catch {
      // silently fail
    }
    setLoaded(true);
  }, [loaded]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const buildEventPages = useCallback(
    (event: SearchEvent, category: string): SearchItem[] => {
      // Resolve the event-role's effective capabilities (CUSTOM → server-
      // projected list; standard roles → IMPLICIT_CAPS) and only offer pages
      // the role may actually open — mirroring the sidebar gates so a CUSTOM
      // "individual" role can't jump to a locked-door section from ⌘K.
      const caps = resolveEventCaps(event.role, event.capabilities as Capability[]);
      const isCustom = event.role === "CUSTOM";
      const isOwnerOrAdmin = event.role === "OWNER" || event.role === "ADMIN";

      type PageDef = GatedPage & {
        icon: React.ElementType;
        label: string;
        /** Builder/external pages use an absolute URL instead of an event sub-path. */
        absoluteUrl?: string;
        /**
         * Pages whose render-time guard hard-requires a legacy role ∈
         * {OWNER,ADMIN} ON TOP OF the capability (settings/general|rules|plugins).
         * A cap-only gate would surface these to a CUSTOM/EDITOR/VIEWER holding
         * the read cap → SNoAccess locked door. See the page-guard audit.
         */
        ownerAdminOnly?: boolean;
        /**
         * Caps the destination ADDITIONALLY hard-requires (ALL of them, on top
         * of the any-of `caps` gate) — for entries whose landing page guards on
         * a different capability than the entry's own domain.
         */
        alsoRequires?: Capability[];
      };

      const pages: PageDef[] = [
        { icon: IconLayoutDashboard, label: t("dashboard"), path: "dashboard", caps: ["dashboards:read"], plugin: Plugin.DASHBOARD },
        // Goals / Attendee app / Documents were missing entirely: the sidebar
        // offers them, ⌘K didn't — for a goals-only CUSTOM role that left the
        // event with no findable entry at all.
        { icon: IconTarget, label: t("goals"), path: "goals", caps: ["event-goals:read"], plugin: Plugin.GOALS },
        { icon: IconDeviceMobile, label: t("attendeeApp"), path: "app", caps: ["event:settings:write"], plugin: Plugin.ATTENDEE_APP },
        { icon: IconWorld, label: t("website"), path: "website", caps: ["website:read"], plugin: Plugin.WEBSITE },
        { icon: IconList, label: t("guestlist"), path: "guests/guestlist", caps: ["guests:read"] },
        { icon: IconInbox, label: t("requests"), path: "guests/requests", caps: ["guests:read.companions"] },
        { icon: IconHistory, label: t("checkInHistory"), path: "check-in/history", caps: ["check-in-logs:read"], plugin: Plugin.CHECK_IN },
        { icon: IconDevices, label: t("checkInDevices"), path: "check-in/devices", caps: ["check-in-devices:read"], plugin: Plugin.CHECK_IN },
        { icon: IconBolt, label: t("automation"), path: "automation/workflows", caps: ["workflows:read"], plugin: Plugin.ACTIONS },
        { icon: IconBolt, label: t("automationLogs"), path: "automation/history", caps: ["workflows:read"], plugin: Plugin.ACTIONS },
        { icon: IconArmchair, label: t("seating"), path: "seating", caps: ["seating:read"], plugin: Plugin.SEATING },
        { icon: IconPaperclip, label: t("attachments"), path: "attachments", caps: ["attachments:read"], plugin: Plugin.ATTACHMENTS },
        { icon: IconFileText, label: t("documents"), path: "documents", caps: ["documents:read"], plugin: Plugin.DOCUMENTS },
        // Programme/Speakers/Sponsors — flat entries mirroring the sidebar.
        // No separate "Appointment slots" row: bookable programme items are
        // part of Programme (one switch, PROGRAM ⇔ SLOTTING), and that row led
        // to this same page. So Programme opens on EITHER half, like the
        // sidebar, TCCCTA and the event landing — a row stored SLOTTING-only
        // before the pair rule keeps its door.
        { icon: IconListDetails, label: t("contentProgramme"), path: "programme", caps: ["event-program:read"], anyPlugin: [Plugin.PROGRAM, Plugin.SLOTTING] },
        { icon: IconMicrophone, label: t("contentSpeakers"), path: "speakers", caps: ["event-speakers:read"], plugin: Plugin.SPEAKERS },
        { icon: IconBuildingStore, label: t("contentSponsors"), path: "sponsors", caps: ["event-sponsors:read"], plugin: Plugin.SPONSORS },
        // Settings → Media (event image library; no plugin gate, page guards
        // event:settings:read itself).
        { icon: IconPhoto, label: t("settingsMedia"), path: "settings/media", caps: ["event:settings:read"] },
        { icon: IconMail, label: t("mailTemplates"), path: "messaging/mail-templates", caps: ["email-templates:read"], plugin: Plugin.MESSAGING },
        { icon: IconSend, label: t("sentMails"), path: "messaging/sent-mails", caps: ["sent-emails:read"], plugin: Plugin.MESSAGING },
        { icon: IconMailForward, label: t("sender"), path: "messaging/sender", caps: ["event:settings:write"], plugin: Plugin.MESSAGING },
        // SMS plugin wave (F-5): "Sent SMS", the SMS page every SMS reader has (as `/sms`).
        ...(smsChannelEnabled
          ? [{ icon: IconMessage, label: t("sms"), path: "sms/sent-sms", caps: ["workflows:read"] as Capability[], plugin: Plugin.SMS }]
          : []),
        // SMS templates wave (F-4): the event's SMS templates (as "Mail templates").
        ...(smsChannelEnabled
          ? [{ icon: IconTemplate, label: t("smsTemplates"), path: "sms/templates", caps: ["workflows:read"] as Capability[], plugin: Plugin.SMS }]
          : []),
        { icon: IconAdjustments, label: t("generalSettings"), path: "settings/general", caps: ["event:settings:write"], ownerAdminOnly: true },
        { icon: IconScale, label: t("rulesAndLimits"), path: "settings/rules", caps: ["check-in-rules:read"], ownerAdminOnly: true },
        { icon: IconPuzzle, label: t("plugins"), path: "settings/plugins", caps: ["event:settings:write"], ownerAdminOnly: true },
        { icon: IconBellRinging, label: t("settingsNotifications"), path: "settings/notifications", caps: ["event:settings:write"], ownerAdminOnly: true },
        // Access page is reachable by CUSTOM users without event-roles:read
        // (their read-only "My access" view) — mirror the sidebar predicate.
        {
          icon: IconUserPlus,
          label: t("access"),
          path: "settings/access",
          caps: isCustom ? [] : ["event-roles:read"],
        },
      ];

      const items: SearchItem[] = pages
        .filter(
          (p) =>
            isPageVisible(p, caps, event.plugins) &&
            (!p.ownerAdminOnly || isOwnerOrAdmin) &&
            (p.alsoRequires ?? []).every((c) => caps.has(c)),
        )
        .map((p) => ({
          icon: p.icon,
          label: p.label,
          category,
          breadcrumb: event.name,
          url: p.absoluteUrl ?? `/event/${event.eventId}/${p.path}`,
          archived: event.archived,
        }));

      // Website Builder + Configurator: edit-grade surfaces, gated on
      // website:write (was role !== VIEWER) and never offered on archived events.
      if (
        event.plugins.includes(Plugin.WEBSITE) &&
        !event.archived &&
        caps.has("website:write")
      ) {
        // Insert right after the Website page to preserve the prior ordering.
        const websiteIdx = items.findIndex((i) =>
          i.url.endsWith(`/event/${event.eventId}/website`),
        );
        const builderItems: SearchItem[] = [
          {
            icon: IconColorFilter,
            label: "Website Builder",
            category,
            breadcrumb: event.name,
            url: `/website-builder/${event.eventId}/root`,
          },
          {
            icon: IconAdjustments,
            label: "Website Configurator",
            category,
            breadcrumb: event.name,
            url: `/website-builder/${event.eventId}/configurator`,
          },
        ];
        items.splice(websiteIdx + 1, 0, ...builderItems);
      }

      return items;
    },
    [t, smsChannelEnabled],
  );

  const buildTemplatePages = useCallback(
    (template: SearchTemplate): SearchItem[] => {
      const sectionMeta: Record<
        TemplateSectionType,
        { icon: React.ElementType; url: string; label: string }
      > = {
        [TemplateSectionType.GENERAL]: {
          icon: IconAdjustments,
          url: "general",
          label: t("generalSettings"),
        },
        [TemplateSectionType.DASHBOARD]: {
          icon: IconLayoutDashboard,
          url: "dashboard",
          label: t("dashboard"),
        },
        [TemplateSectionType.WEBSITE]: {
          icon: IconWorld,
          url: "website",
          label: t("website"),
        },
        [TemplateSectionType.FIELDS]: {
          icon: IconUsers,
          url: "fields",
          label: "Fields",
        },
        [TemplateSectionType.DEVICES]: {
          icon: IconDevices,
          url: "devices",
          label: t("checkInDevices"),
        },
        [TemplateSectionType.SLOTTING]: {
          icon: IconCalendarEvent,
          url: "slotting",
          label: t("slotting"),
        },
        [TemplateSectionType.SEATING]: {
          icon: IconArmchair,
          url: "seating",
          label: t("seating"),
        },
        [TemplateSectionType.PIPES]: {
          icon: IconBolt,
          url: "workflows",
          label: t("automationWorkflows"),
        },
        [TemplateSectionType.EMAIL]: {
          icon: IconMail,
          url: "email",
          label: t("mailTemplates"),
        },
        [TemplateSectionType.RULES]: {
          icon: IconScale,
          url: "rules",
          label: t("rulesAndLimits"),
        },
        [TemplateSectionType.PLUGINS]: {
          icon: IconPuzzle,
          url: "plugins",
          label: t("plugins"),
        },
        [TemplateSectionType.SENDER]: {
          icon: IconMailForward,
          url: "sender",
          label: t("sender"),
        },
        [TemplateSectionType.ATTACHMENTS]: {
          icon: IconPaperclip,
          url: "attachments",
          label: t("attachments"),
        },
        [TemplateSectionType.DOCUMENTS]: {
          icon: IconFileText,
          url: "documents",
          label: t("documents"),
        },
        [TemplateSectionType.COLLABORATORS]: {
          icon: IconUserPlus,
          url: "collaborators",
          label: t("collaborators"),
        },
        [TemplateSectionType.GOALS]: {
          icon: IconTargetArrow,
          url: "goals",
          label: tTemplates("sections.goals"),
        },
        [TemplateSectionType.TABLE_VIEWS]: {
          icon: IconTable,
          url: "table-views",
          label: tTemplates("sections.tableViews"),
        },
        [TemplateSectionType.GUEST_IMPORT]: {
          icon: IconAddressBook,
          url: "guest-import",
          label: tTemplates("sections.guestImport"),
        },
      };

      const orderedSections: TemplateSectionType[] = [
        TemplateSectionType.GENERAL,
        TemplateSectionType.DASHBOARD,
        TemplateSectionType.WEBSITE,
        TemplateSectionType.FIELDS,
        TemplateSectionType.DEVICES,
        TemplateSectionType.SLOTTING,
        TemplateSectionType.SEATING,
        TemplateSectionType.PIPES,
        TemplateSectionType.EMAIL,
        TemplateSectionType.GOALS,
        TemplateSectionType.GUEST_IMPORT,
        TemplateSectionType.TABLE_VIEWS,
        TemplateSectionType.RULES,
        TemplateSectionType.COLLABORATORS,
        TemplateSectionType.PLUGINS,
        TemplateSectionType.SENDER,
        TemplateSectionType.ATTACHMENTS,
        TemplateSectionType.DOCUMENTS,
      ];

      const enabledSections = new Set<TemplateSectionType>([
        TemplateSectionType.GENERAL,
        ...(template.includedSections ?? []),
      ]);

      const items: SearchItem[] = [];
      for (const section of orderedSections) {
        if (!enabledSections.has(section)) continue;
        const meta = sectionMeta[section];
        items.push({
          icon: meta.icon,
          label: template.name,
          category: t("templates"),
          breadcrumb: meta.label,
          url: `/templates/${template.templateId}/${meta.url}`,
        });
      }

      return items;
    },
    [t, tTemplates],
  );

  const allItems = useMemo((): SearchItem[] => {
    const items: SearchItem[] = [];

    // Pinned first so it's the default-selected row: ⌘K then Enter opens the
    // assistant. Its own one-row group (category = assistant name) keeps it
    // visually at the top without merging into Navigation.
    if (showAiItem) {
      items.push({
        icon: IconSparkles,
        label: tAi("open"),
        category: tAi("title"),
        url: "#ai-assistant",
        action: () =>
          window.dispatchEvent(
            new Event("openAiAssistant", { cancelable: true }),
          ),
      });
    }

    const currentEvent = currentEventId
      ? events.find((e) => e.eventId === currentEventId)
      : null;

    if (currentEvent) {
      items.push(...buildEventPages(currentEvent, t("currentEvent")));
    }

    for (const event of events) {
      items.push({
        icon: IconCalendarEvent,
        label: event.name,
        category: t("events"),
        // Land on the first section this role may open, not a maybe-forbidden
        // dashboard (CUSTOM roles often lack dashboards:read).
        url: firstAccessibleEventUrl(
          event.eventId,
          event.role,
          event.plugins,
          event.capabilities as Capability[],
        ),
        archived: event.archived,
        eventId: event.eventId,
      });
    }

    for (const event of events) {
      if (event.eventId === currentEventId) continue;
      items.push(...buildEventPages(event, t("eventPages")));
    }

    items.push({
      icon: IconHome,
      label: t("home"),
      category: t("navigation"),
      url: "/",
    });
    items.push({
      icon: IconPlus,
      label: t("createEvent"),
      category: t("navigation"),
      url: "/create-event",
    });
    items.push({
      icon: IconTemplate,
      label: t("templates"),
      category: t("navigation"),
      url: "/templates",
    });
    for (const template of templates) {
      items.push(...buildTemplatePages(template));
    }

    // Use same labels and icons as UserSidebar for consistency
    items.push({
      icon: IconUser,
      label: tUser("profileSettings"),
      category: t("settingsCategory"),
      url: "/user/settings",
    });
    items.push({
      icon: IconColorFilter,
      label: tUser("appearanceSettings"),
      category: t("settingsCategory"),
      url: "/user/theme",
    });
    items.push({
      icon: IconShieldHalf,
      label: t("security"),
      category: t("settingsCategory"),
      url: "/user/security",
    });

    if (companyRole !== "VIEWER" && companyRole !== "EDITOR") {
      items.push({
        icon: IconBuilding,
        label: tUser(customerType === "COMPANY" ? "myCompany" : "myAccount"),
        category: t("settingsCategory"),
        url: "/user/company",
      });
      items.push({
        icon: IconUsers,
        label: tUser("collaborators"),
        category: t("settingsCategory"),
        url: "/user/collaborators",
      });
      items.push({
        icon: IconAt,
        label: tUser("emailDomains"),
        category: t("settingsCategory"),
        url: "/user/domains",
      });
    }

    if (companyRole === "OWNER") {
      items.push({
        icon: IconCrown,
        label: t("subscription"),
        category: t("settingsCategory"),
        url: "/user/subscription",
      });
    }

    const deduped: SearchItem[] = [];
    const seenUrls = new Set<string>();
    for (const item of items) {
      if (seenUrls.has(item.url)) continue;
      seenUrls.add(item.url);
      deduped.push(item);
    }

    return deduped;
  }, [events, templates, companyRole, customerType, currentEventId, showAiItem, buildEventPages, buildTemplatePages, t, tUser, tAi]);

  // Parse raw input into pageQuery (what to search) + atTokens (event scope)
  const parsedQuery = useMemo(() => parseQuery(searchState), [searchState]);

  // Events matched by @tokens — null means no @ scoping active
  const atScopedEvents = useMemo((): SearchEvent[] | null => {
    const { atTokens } = parsedQuery;
    if (atTokens.length === 0) return null;
    const matched = events.filter((e) =>
      atTokens.every((token) =>
        e.name.toLowerCase().includes(token.toLowerCase()),
      ),
    );
    // If no event matched, fall back to treating input as normal search
    return matched.length > 0 ? matched : null;
  }, [parsedQuery, events]);

  const lockedEventPages = useMemo((): SearchItem[] => {
    if (!lockedEvent) return [];
    return buildEventPages(lockedEvent, t("eventPages"));
  }, [lockedEvent, buildEventPages, t]);

  const searchResults = useMemo((): SearchItem[] => {
    const { pageQuery, hasAt, atTokens } = parsedQuery;

    // Locked event mode: scope to that event's pages with scoring
    if (lockedEvent) {
      if (!pageQuery) return lockedEventPages;
      return lockedEventPages
        .map((item) => ({ item, score: scoreItem(item, pageQuery) }))
        .filter((x) => x.score > 0)
        .sort((a, b) => b.score - a.score)
        .map((x) => x.item);
    }

    // @-picker mode: show only matching events so user can pick one
    if (hasAt) {
      const token = atTokens[0]?.toLowerCase() ?? "";
      const eventItems = allItems.filter((item) => !!item.eventId);
      if (!token) return eventItems;
      return eventItems.filter((item) =>
        item.label.toLowerCase().includes(token),
      );
    }

    // Normal browse mode (no query): hide eventPages category
    if (!pageQuery) {
      return allItems.filter((item) => item.category !== t("eventPages"));
    }

    // Scored search across all items
    return allItems
      .map((item) => ({ item, score: scoreItem(item, pageQuery) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.item);
  }, [parsedQuery, allItems, lockedEvent, lockedEventPages, t]);

  const lockEvent = useCallback(
    (eventId: string) => {
      const event = events.find((e) => e.eventId === eventId);
      if (event) {
        setLockedEvent(event);
        // Preserve any page query typed alongside @event, clear only the @ part
        setSearchState((current) => parseQuery(current).pageQuery);
      }
    },
    [events],
  );

  const unlockEvent = useCallback(() => {
    setLockedEvent(null);
    setSearchState("");
  }, []);

  // Auto-unlock locked event when user starts typing @ (@ overrides locked mode)
  const handleSearchInput = useCallback(
    (value: string) => {
      if (lockedEvent && value.includes("@")) {
        setLockedEvent(null);
      }
      setSearchState(value);
    },
    [lockedEvent],
  );

  // Ghost text: suffix to complete when top result's label starts with the typed query
  const ghostText = useMemo(() => {
    const q = parsedQuery.pageQuery;
    if (!q) return "";
    const top = searchResults[0];
    if (!top) return "";
    if (top.label.toLowerCase().startsWith(q.toLowerCase())) {
      return top.label.slice(q.length);
    }
    return "";
  }, [parsedQuery.pageQuery, searchResults]);

  // @-token ghost text: complete the event name when the user is actively typing @something
  // at the end of the input (e.g. "@a" → ghost "sdf" when event "asdf" matches)
  const atGhostText = useMemo(() => {
    const { atTokens } = parsedQuery;
    if (atTokens.length === 0 || !atScopedEvents || atScopedEvents.length === 0)
      return "";
    const lastToken = atTokens[atTokens.length - 1];
    // Only complete when the input ends with this exact @token
    if (
      !searchState
        .trimEnd()
        .toLowerCase()
        .endsWith(`@${lastToken.toLowerCase()}`)
    )
      return "";
    const bestEvent = atScopedEvents[0];
    if (bestEvent.name.toLowerCase().startsWith(lastToken.toLowerCase())) {
      return bestEvent.name.slice(lastToken.length);
    }
    return "";
  }, [parsedQuery, atScopedEvents, searchState]);

  const isAtPicker = !lockedEvent && parsedQuery.hasAt;

  return {
    searchState,
    setSearchState: handleSearchInput,
    searchResults,
    loadData,
    lockedEvent,
    lockEvent,
    unlockEvent,
    isAtPicker,
    parsedQuery,
    ghostText,
    atGhostText,
  };
};
